/**
 * PresetChange — recall a preset on an external device (DEC-148).
 *
 * Its own item kind rather than a Button action type because one preset
 * change is TWO ordered MIDI messages. See `PresetChangeItem` in
 * projectStore.ts for the wire format and the channel-base note.
 *
 * ## Why it reuses `.midi-button`
 *
 * The face is a button face. Reusing the exact classes FireButton uses means
 * this tile is skinned identically across all seven skins with zero new CSS —
 * which is the DEC-121 "no per-skin structural CSS" rule, and keeps
 * `skin_matches_workbench` meaningful rather than adding a surface it does
 * not know about.
 *
 * ## Dead state is loud, per DEC-138
 *
 * A pedal lives on its OWN MIDI port, not the rig's default (which is
 * Ableton's). port/channel resolve LIVE from the tile's bound `GuitarPedal`
 * (Connected Devices, DEC-152 amended 2026-08-04) — no pedal bound, or one
 * that's been removed, means "this tile cannot possibly work", and it says
 * so on its face instead of tapping silently into Ableton. Same reasoning
 * as the unpaired-bridge pill.
 */

import { createSignal, Show, type Accessor } from 'solid-js';
import { editOwnsGesture, selectedItemId, type PresetChangeItem, type LayoutColor } from '../layout/store.ts';
import { activePedals } from '../layout/projectStore.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { fireAction, type FireResult } from '../opbridge/dispatch.ts';
import { bridgeConnectedReactive } from '../opbridge/store.ts';
import { ulid } from '../utils/ulid.ts';
import { recordHelixCommand, helixLastCommanded } from '../helix/lastCommanded.ts';
import { helixPresetName } from '../helix/library.ts';
import { pushToast } from '../ui/toast.ts';
import { IconUnlinked } from './PedalStatusIcon.tsx';
import type { MidiCcStep, MidiProgramChangeStep } from '@opbuttons/schema';
import { pedalBridgeRowId } from '../pedals/bridgeBinding.ts';
import { timelineLit } from '../timeline/canvasLights.ts';
import { PadDecor, padAttrs } from './PadDecor.tsx';

/** Helix reserved CCs — official 3.80 Owner's Manual, verified on hardware
 *  2026-08-01. These are DEVICE-DEFINED, not rig-allocatable: they are not
 *  drawn from the project's rolling CC counter and must never be treated as
 *  free. */
const CC_SETLIST = 32;
const CC_SNAPSHOT = 69;

interface PresetChangeProps {
  item: PresetChangeItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** "02B" — the Helix's own bank+letter notation, which is what is printed on
 *  the unit. PC 5 reads as 02B: bank = floor(pc/4)+1, letter = pc%4. Showing
 *  the raw PC number instead would be technically correct and useless at a
 *  glance on stage. */
export function helixSlotLabel(program: number): string {
  const bank = Math.floor(program / 4) + 1;
  const letter = ['A', 'B', 'C', 'D'][program % 4] ?? 'A';
  return `${String(bank).padStart(2, '0')}${letter}`;
}

export function PresetChange(props: PresetChangeProps) {
  const guard = createDiscreteGuard(() => !!props.item.guard);
  const [pressed, setPressed] = createSignal(false);
  const [fault, setFault] = createSignal(false);

  /** The pedal this tile is bound to, resolved LIVE every read — never
   *  cached on the item. Absent `pedalId`, or one pointing at a removed
   *  pedal, resolves to `undefined` here and the tile goes dead below. */
  const pedal = () => {
    const id = props.item.pedalId;
    return id ? activePedals().find((pd) => pd.id === id) : undefined;
  };
  /** No bound pedal (or no port on it yet) = cannot work. Checked
   *  separately from transport so the face can say WHICH thing is wrong;
   *  "nothing happens" is the failure mode this whole item exists to avoid. */
  const noPort = () => !pedal()?.port;
  /** Liveness tracks the SPECIFIC bridge this pedal is wired to (per-item
   *  bridge affinity) — not just "some bridge is connected" — so a Helix
   *  pedal bound to the Mac shows dead if only the dev PC is up. */
  const live = () => bridgeConnectedReactive(pedalBridgeRowId(pedal())) && !noPort();
  /** True specifically when the pedal is properly authored (bound + ported)
   *  but its bridge is offline — "in the mesh, unplugged right now" — as
   *  opposed to an authoring gap (no pedal bound / pedal removed / no port
   *  set). Drives the unlinked glyph; the other fault reasons don't get one
   *  since they're not a connectivity state. */
  const isDisconnected = () => !!pedal() && !noPort() && !bridgeConnectedReactive(pedalBridgeRowId(pedal()));

  /** The compound fire. Ordered and awaited: the setlist CC must land before
   *  the PC or the preset resolves against whatever setlist is currently
   *  loaded — which the player may have moved by hand at the unit.
   *
   *  No inter-message delay: the Helix manual states a CC69 snapshot change
   *  received DURING a preset load is buffered and applied once the load
   *  finishes, so the three can go back-to-back. Do not add sleeps here
   *  without re-reading that passage. */
  async function fire(): Promise<void> {
    const pd = pedal();
    const port = pd?.port;
    if (!port) return;
    const channel = pd.channel + 1; // item is 0-based, schema is 1-16
    const base = { target: pedalBridgeRowId(pd) ?? '', port };

    const setlistStep: MidiCcStep = {
      ...base, type: 'midi.cc', id: ulid(),
      channel, cc: CC_SETLIST, value: props.item.setlist & 0x7f,
    };
    const presetStep: MidiProgramChangeStep = {
      ...base, type: 'midi.pc', id: ulid(),
      channel, program: props.item.program & 0x7f,
    };

    const results: FireResult[] = [];
    results.push(...await fireAction(setlistStep));
    results.push(...await fireAction(presetStep));

    // Snapshot is optional and only sent when the author set one — recalling
    // a preset should not silently move a snapshot the player chose by hand.
    if (typeof props.item.snapshot === 'number') {
      const snapStep: MidiCcStep = {
        ...base, type: 'midi.cc', id: ulid(),
        channel, cc: CC_SNAPSHOT, value: props.item.snapshot & 0x7f,
      };
      results.push(...await fireAction(snapStep));
    }

    // DEC-124's principle: report what actually happened. A partial send is a
    // FAULT, not a success — landing the setlist but not the preset leaves the
    // pedal somewhere the author never asked for, which is worse than a no-op.
    const ok = results.length > 0 && results.every((r) => r.kind === 'ok');
    setFault(!ok);
    // "Last commanded" tracker for a DisplayItem bound to this pedal (only on
    // success - a failed send didn't actually change anything on the unit).
    if (ok) {
      recordHelixCommand(pd.id, {
        kind: 'preset',
        setlist: props.item.setlist,
        program: props.item.program,
        presetName: faceText(),
        // Ride-along snapshot (DEC-151) - only present when the author set
        // one, so a Display's Helix face can show both.
        ...(typeof props.item.snapshot === 'number' ? { snapshot: props.item.snapshot } : {}),
        at: Date.now(),
      });
    }
  }

  function onPointerDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return; // right-click escape hatch
    if (!live()) {
      // Tapping a dead tile used to be indistinguishable from a missed
      // touch — the face already says why (subText), but that reads best
      // at rest, not mid-set. A tap surfaces it loud, once, at the moment
      // the operator actually reached for it.
      pushToast('warn', `${faceText()} - ${faultText() ?? 'send failed'}`);
      return;
    }
    e.stopPropagation();
    if (!guard.gate('fire')) return; // guarded: first tap only arms
    setFault(false);
    setPressed(true);
    setTimeout(() => setPressed(false), 120);
    void fire();
  }

  /** Name when we have one, the device's own slot notation otherwise. The
   *  name arrives from a probed/imported library and is cached on the item so
   *  the face still reads correctly with the bridge offline. The latest read
   *  leads: a preset renamed on the pedal shows its new name once read
   *  (operator, 2026-10-10: "tiles should update if they've been superseded
   *  in the list"); the cache is the fallback when this device has no list. */
  const faceText = () => props.item.name
    ?? helixPresetName(props.item.setlist, props.item.program)
    ?? props.item.presetName
    ?? helixSlotLabel(props.item.program);

  /* FACE FLAGS (2026-08-09). The Inspector offers "Show name" / "Show info"
     on every kind and this one honoured neither.
     `hideLabel` is overridden by a DEAD STATE, exactly as FireButton already
     does it: every branch above the last in `subText` is an error message,
     and letting a face flag suppress "no device assigned" would turn a
     visible fault into a button that silently does nothing on stage. The flag
     hides the ROUTINE readout only. */
  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel || isFault();
  const isFault = () =>
    !props.item.pedalId || !pedal() || noPort() || !bridgeConnectedReactive(pedalBridgeRowId(pedal())) || fault();

  /** Dead-state / fault message, or `null` when the tile is healthy and
   *  should show its normal SL/slot/snap readout instead — split out
   *  (2026-08-18) so the slot notation can be rendered as its own bigger
   *  badge below, rather than folded into one plain string. */
  const faultText = () => {
    if (!props.item.pedalId) return 'no device assigned';
    if (!pedal()) return 'device removed';
    if (noPort()) return 'no device port set';
    if (!bridgeConnectedReactive(pedalBridgeRowId(pedal()))) return 'no bridge';
    // A failed send reports in TEXT, not a colour. `data-fault` was tried and
    // removed: nothing in any stylesheet paints it, so the fault rendered
    // identically to success — a dead attribute, same failure class as a dead
    // CSS selector. Adding a rule for it would also mean a state that exists
    // in the app but not in design.html, which `skin_matches_workbench` is
    // there to prevent. Text costs nothing and reads at arm's length.
    if (fault()) return 'send failed';
    return null;
  };

  /** This tile's preset is the last one commanded to its pedal (helix/lastCommanded.ts): SENT, never confirmed, since a
   *  Helix never reports back (DEC-321 rule 1). */
  const sentHere = (): boolean => {
    const c = props.item.pedalId ? helixLastCommanded()[props.item.pedalId] : undefined;
    return !!c && c.kind === 'preset' && c.setlist === props.item.setlist && c.program === props.item.program;
  };

  return (
    <button
      class="midi-button"
      {...padAttrs(props.item)}
      type="button"
      data-sent={sentHere() ? 'true' : undefined}
      data-on={timelineLit(props.item.id) ? 'true' : 'false'}
      data-pressed={pressed() ? 'true' : 'false'}
      data-armed={guard.armedKey() === 'fire' ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape="rect"
      data-family="rect"
      onPointerDown={onPointerDown}
      data-disabled={live() ? 'false' : 'true'}
    >
      <PadDecor item={props.item} />
      {/* SENT (DEC-321 rule 1): what only OPButtons knows; the device never reports, so never the flood. */}
      <Show when={sentHere()}><span class="sent-tag" aria-label="sent">SENT</span></Show>
      {showName() && (
        <span class="midi-button-name">
          <span class="face-line">{faceText()}</span>
        </span>
      )}
      {showLabel() && (
        <span class="midi-button-label">
          {isDisconnected() && <IconUnlinked />}
          <Show when={faultText()} fallback={
            <>
              {`SL${props.item.setlist + 1} · `}
              {/* 2026-08-18 (operator ask: "make it bigger in the canvas
                  one") - the slot notation is the one piece of this face
                  an operator actually verifies against the physical unit,
                  so it gets its own larger, bolder treatment instead of
                  blending into the rest of the small info line. */}
              <span class="preset-slot-badge">{helixSlotLabel(props.item.program)}</span>
              {typeof props.item.snapshot === 'number' && ` · snap ${props.item.snapshot + 1}`}
            </>
          }>
            {faultText()}
          </Show>
        </span>
      )}
    </button>
  );
}
